import { useState } from "react";
import { format } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { IssueActivity } from "@shared/schema";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, AlertCircle } from "lucide-react";
import { ScreenshotAttachmentIndicator } from "@/components/issues/screenshot-attachment-indicator";
import { EditNoteDialog } from "@/components/issues/edit-note-dialog";

interface ActivityTimelineProps {
  activities: IssueActivity[];
  issueId: string;
}

const ACTIVITY_ICONS: Record<string, string> = {
  created: "✅",
  updated: "📝",
  status_changed: "🔄",
  severity_changed: "⚠️",
  priority_changed: "🎯",
  assigned: "👤",
  unassigned: "👤",
  resolved: "✅",
  closed: "✅",
  reopened: "🔓",
  field_changed: "📝",
  note_added: "💬",
  linked_commit: "🔗",
  user_rejected: "🔁",
};

const ACTIVITY_LABELS: Record<string, string> = {
  created: "Created issue",
  updated: "Updated issue",
  status_changed: "Status changed",
  severity_changed: "Severity changed",
  priority_changed: "Priority changed",
  assigned: "Assigned",
  unassigned: "Unassigned",
  resolved: "Resolved",
  closed: "Closed",
  reopened: "Reopened",
  field_changed: "Field changed",
  note_added: "Note added",
  linked_commit: "Linked commit",
  user_rejected: "Rework Requested",
};

const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  description: "Description",
  status: "Status",
  severity: "Severity",
  priority: "Priority",
  module: "Module",
  assigned_to: "Assigned To",
  root_cause: "Root Cause",
  solution: "Solution",
  prevention: "Prevention",
};

export function ActivityTimeline({ activities, issueId }: ActivityTimelineProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<IssueActivity | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteNoteMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const res = await apiRequest("DELETE", `/api/crm/issues/${issueId}/activity/${activityId}`);
      return res.json();
    },
    onSuccess: () => {
      // Invalidate activity log to refresh
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/issues", issueId, "activity"]
      });

      // Show success toast
      toast({
        title: "Note deleted",
        description: "The note has been deleted successfully.",
      });

      // Close dialog
      setDeleteDialogOpen(false);
      setSelectedActivity(null);
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: "Failed to delete note",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEditClick = (activity: IssueActivity) => {
    setSelectedActivity(activity);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (activity: IssueActivity) => {
    setSelectedActivity(activity);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedActivity) {
      deleteNoteMutation.mutate(selectedActivity.id);
    }
  };

  if (!activities || activities.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center">
        <p className="text-muted-foreground text-sm">No activity recorded yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: "180px" }}>Type</TableHead>
              <TableHead>Details</TableHead>
              <TableHead style={{ width: "150px" }}>Changed By</TableHead>
              <TableHead style={{ width: "160px" }}>Date</TableHead>
              <TableHead style={{ width: "100px" }}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activities.map((activity) => {
              const isNoteActivity = activity.activityType === "note_added";
              const isAgentOrSystem = activity.changedByName?.includes("Agent") ||
                                      activity.changedByName === "System" ||
                                      !activity.changedByName;
              const isUserNote = isNoteActivity && !isAgentOrSystem;
              const isReworkRequest = activity.activityType === "user_rejected";

              return (
                <TableRow
                  key={activity.id}
                  className={isReworkRequest ? "bg-orange-50 border-l-4 border-l-orange-500" : ""}
                >
                  {/* Type Column */}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {isReworkRequest ? (
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      ) : (
                        <span className="text-lg">
                          {ACTIVITY_ICONS[activity.activityType] || "📝"}
                        </span>
                      )}
                      <div className="flex flex-col gap-1">
                        <span className={`text-xs font-medium ${isReworkRequest ? "text-orange-700 font-semibold" : ""}`}>
                          {ACTIVITY_LABELS[activity.activityType] || activity.activityType}
                        </span>
                        {activity.fieldName && (
                          <Badge variant="outline" className="text-xs w-fit">
                            {FIELD_LABELS[activity.fieldName] || activity.fieldName}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Details Column */}
                  <TableCell>
                    {/* Field changes */}
                    {activity.oldValue && activity.newValue && (
                      <div className="text-xs text-muted-foreground mb-1">
                        <span className="line-through opacity-60">{activity.oldValue}</span>
                        {" → "}
                        <span className="font-medium text-foreground">{activity.newValue}</span>
                      </div>
                    )}

                    {/* Notes */}
                    {activity.notes && (
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap mb-1">
                        {activity.notes}
                      </p>
                    )}

                    {/* Screenshot attachments for notes */}
                    {isUserNote && (
                      <ScreenshotAttachmentIndicator issueId={issueId} activityLogId={activity.id} />
                    )}
                  </TableCell>

                  {/* Changed By Column */}
                  <TableCell>
                    <span className="text-xs">
                      {activity.changedByName || "System"}
                    </span>
                  </TableCell>

                  {/* Date Column */}
                  <TableCell>
                    <div className="flex flex-col text-xs text-muted-foreground">
                      <span>{format(new Date(activity.createdAt), "MMM d, yyyy")}</span>
                      <span>{format(new Date(activity.createdAt), "HH:mm")}</span>
                    </div>
                  </TableCell>

                  {/* Actions Column */}
                  <TableCell>
                    {isUserNote && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(activity)}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(activity)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit Note Dialog */}
      {selectedActivity && (
        <EditNoteDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          issueId={issueId}
          activityId={selectedActivity.id}
          currentNote={selectedActivity.notes || ""}
          originalAuthor={selectedActivity.changedByName || "Unknown"}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
